import { Alert, Typography, Box, CircularProgress } from "@mui/material";
import { useEffect, useState, HTMLProps } from "react";

// Extend HTMLProps<HTMLImageElement> to make this compatible with ReactMarkdown
export type ImageProps = HTMLProps<HTMLImageElement> & {
  src?: string;
  alt?: string;
};

export default function ChatImage(props: ImageProps) {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  // Handle image load events
  const handleImageLoad = () => {
    setLoading(false);
  };

  const handleImageError = () => {
    setLoading(false);
    setError(true);
    console.error("Error loading image:", props.src?.substring(0, 100) + "...");
  };

  // Check if we actually have a source URL
  if (!props.src) {
    return <Alert severity="error">No image source provided</Alert>;
  }

  // Check if this is a GPT-image-1 response - look for tag (backward compatibility) or model info
  const isGptImage = props.alt?.includes("[GPT-image-1]") || false;
  
  // Get the actual user prompt
  let originalPrompt = props.alt || "Generated image";
  
  // Debug: Log all properties we get for the image
  console.log("ChatImage props:", { 
    src: props.src?.substring(0, 50) + "...", 
    alt: props.alt, 
    isGptImage 
  });
  
  // COMPLETELY NEW APPROACH: Direct text replacement right before display
  const displayPrompt = (() => {
    // Start with whatever we have as original prompt
    let finalPrompt = originalPrompt;
    
    // Remove any GPT-image tag with a direct string replace
    if (typeof finalPrompt === 'string') {
      // Handle complete tag
      finalPrompt = finalPrompt.replace('[GPT-image-1]', '');
      
      // Handle incomplete tag
      finalPrompt = finalPrompt.replace('[GPT-image-1', '');
      
      // Handle any residual whitespace
      finalPrompt = finalPrompt.trim();
      
      // If we ended up with nothing, use a fallback
      if (!finalPrompt || finalPrompt === 'Generated image') {
        // Try one more time to extract from DOM
        try {
          const lastUserMessage = document.querySelector('[data-testid="user-message"] .MuiTypography-root');
          if (lastUserMessage && lastUserMessage.textContent) {
            finalPrompt = lastUserMessage.textContent.trim();
            console.log("Final fallback using DOM prompt:", finalPrompt);
          } else {
            finalPrompt = "AI generated image";
          }
        } catch (error) {
          console.error('Failed to get prompt from DOM in final fallback:', error);
          finalPrompt = "AI generated image";
        }
      }
      
      console.log('FINAL DISPLAY PROMPT:', finalPrompt);
    }
    
    return finalPrompt;
  })();

  // Check if this is a valid image URL or data URL
  const isValidSrc = props.src.startsWith('data:') || 
                    props.src.startsWith('http') || 
                    props.src.startsWith('blob:');
  
  // If this is not a valid URL, show an error
  if (!isValidSrc) {
    console.log("Invalid image source:", props.src);
    return (
      <Box sx={{ my: 2 }}>
        <Alert severity="warning">
          Image was generated but can't be displayed. The source doesn't appear to be a valid URL.
        </Alert>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {displayPrompt}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ my: 2 }}>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress />
        </Box>
      )}
      
      {error ? (
        <Alert severity="error">
          Error loading image. The image data may be incomplete or invalid.
        </Alert>
      ) : (
        <>
          <Alert severity="warning">
            Images generated will expire after 1 hour. Please make sure you download
            any images you want to keep.
          </Alert>
          
          <Box sx={{ textAlign: 'center', my: 2 }}>
            <img
              {...props}
              alt={displayPrompt} // Use the cleaned prompt as alt text
              style={{ 
                maxWidth: "100%", 
                borderRadius: "15px",
                display: loading ? 'none' : 'inline-block',
                ...(props.style || {})
              }}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          </Box>
          
          {!loading && !error && (
            <details>
              <Typography variant="overline" component="summary">
                Processed Image Prompt
              </Typography>
              <Typography variant="body2">
                {displayPrompt}
              </Typography>
            </details>
          )}
        </>
      )}
    </Box>
  );
}
